import { escapeRegex, readPattern } from '../GscTools/regex';

/**
 * The content-group vocabulary: how a named URL group is written, what it is sent to Search
 * Console as, and how a returned URL is checked against it again in the browser.
 *
 * A group is a name and the rules under it. Every rule is a filter on the page dimension, and a
 * group's rules are ANDed — they go into Search Console's one "and" filter group together — so a
 * page belongs to a group only when it meets all of them. There is no OR between rules: an OR
 * group changes what a returned row means, and two prefixes that should both count are two groups,
 * or one expression, rather than a group type Search Console cannot be asked for faithfully.
 *
 * Groups are not a partition. Each one is its own filtered request, nothing stops a page from
 * meeting two groups' rules, and such a page is counted in both — which is why no figure on this
 * report is ever added across groups, and why the matching-URL block names the other groups a URL
 * also falls into.
 */

/** How one rule matches a page URL. */
export const OPERATOR = {
    STARTS_WITH: 'starts_with',
    CONTAINS: 'contains',
    NOT_CONTAINS: 'not_contains',
    EQUALS: 'equals',
    MATCHES_REGEX: 'matches_regex',
    EXCLUDES_REGEX: 'excludes_regex',
};

export const OPERATORS = Object.values(OPERATOR);

const REGEX_OPERATORS = new Set([OPERATOR.MATCHES_REGEX, OPERATOR.EXCLUDES_REGEX]);

/**
 * The Search Console operator each rule is sent as.
 *
 * Search Console has no "starts with", so a prefix is sent as an anchored `includingRegex` over
 * the escaped literal: every metacharacter in the URL — the dots above all — is matched as itself,
 * and the anchor keeps a prefix from matching the same path deeper inside another URL. The other
 * literal operators are Search Console's own and their value is sent verbatim.
 */
const API_OPERATOR = {
    [OPERATOR.STARTS_WITH]: 'includingRegex',
    [OPERATOR.CONTAINS]: 'contains',
    [OPERATOR.NOT_CONTAINS]: 'notContains',
    [OPERATOR.EQUALS]: 'equals',
    [OPERATOR.MATCHES_REGEX]: 'includingRegex',
    [OPERATOR.EXCLUDES_REGEX]: 'excludingRegex',
};

/**
 * Product bounds on one session's definitions. Every group costs a totals request per apply, so
 * the number of groups is what bounds the request budget; the rest bound what one rule may hold.
 */
export const MAX_GROUPS = 8;
export const MAX_RULES_PER_GROUP = 5;
export const MAX_NAME_LENGTH = 60;
export const MAX_VALUE_LENGTH = 1000;

/** An empty row of the rule editor. */
export function emptyRule() {
    return { group: '', operator: OPERATOR.STARTS_WITH, value: '' };
}

export function operatorLabel(t, operator) {
    return t(`tools.content_groups.operators.${operator}`);
}

/** A rule as one line of text, for its chip and for an error that names it. */
export function ruleLabel(t, rule) {
    return `${rule.group}: ${operatorLabel(t, rule.operator)} ${rule.value}`;
}

/** The expression one rule is sent with: the escaped, anchored literal for a prefix. */
export function ruleExpression(rule) {
    return rule.operator === OPERATOR.STARTS_WITH ? `^${escapeRegex(rule.value)}` : rule.value;
}

/** The filter one rule becomes, ready to go into a request's "and" group. */
export function ruleFilter(rule) {
    return {
        dimension: 'page',
        operator: API_OPERATOR[rule.operator],
        expression: ruleExpression(rule),
    };
}

/** Every filter one group is requested with. */
export function groupFilters(group) {
    return group.rules.map(ruleFilter);
}

/** A rule's filter as Search Console receives it, as text: what block 01 and the exports print. */
export function filterLabel(rule) {
    const filter = ruleFilter(rule);
    return `${filter.dimension} ${filter.operator} ${filter.expression}`;
}

/** The rules gathered into their groups, in the order each group was first named. */
export function groupsFrom(rules) {
    const groups = new Map();

    for (const rule of rules ?? []) {
        if (!groups.has(rule.group)) groups.set(rule.group, { name: rule.group, rules: [] });
        groups.get(rule.group).rules.push(rule);
    }

    return [...groups.values()];
}

/**
 * Why one rule's condition cannot be sent, whatever group it is in, or null when it can.
 *
 * A prefix that is not a full URL is refused rather than sent: Search Console's page values are
 * absolute URLs, so a prefix such as "/guides/" can only ever match nothing — and an empty answer
 * would read as a group with no traffic rather than as a rule written the wrong way round.
 */
export function conditionError(rule, t) {
    const value = (rule?.value ?? '').trim();

    if (!OPERATORS.includes(rule?.operator)) return t('tools.content_groups.errors.no_operator');
    if (!value) return t('tools.content_groups.errors.no_value');
    if (value.length > MAX_VALUE_LENGTH) {
        return t('tools.content_groups.errors.value_long', { max: MAX_VALUE_LENGTH });
    }

    if (rule.operator === OPERATOR.STARTS_WITH && !/^https?:\/\//i.test(value)) {
        return t('tools.content_groups.errors.prefix_not_url');
    }

    if (REGEX_OPERATORS.has(rule.operator)) {
        try {
            readPattern(value);
        } catch (err) {
            return t('tools.content_groups.errors.invalid_regex', { reason: err.message });
        }
    }

    return null;
}

/** Why one rule cannot be sent — its group name, then its condition — or null when it can. */
export function ruleError(rule, t) {
    const group = (rule?.group ?? '').trim();

    if (!group) return t('tools.content_groups.errors.no_name');
    if (group.length > MAX_NAME_LENGTH) {
        return t('tools.content_groups.errors.name_long', { max: MAX_NAME_LENGTH });
    }

    return conditionError(rule, t);
}

/**
 * Why one typed rule cannot be added to the session's rules, or null when it can.
 *
 * On top of the rule being sendable at all: a rule already in force is not added twice — the
 * request would carry the same filter twice over, which narrows nothing — and the bounds are held
 * here, where the user can see which rule tipped them, rather than only at Apply.
 */
export function addRuleError(rule, rules, t) {
    const error = ruleError(rule, t);
    if (error) return error;

    const groups = groupsFrom(rules);
    const existing = groups.find((group) => group.name === rule.group);

    if (!existing && groups.length >= MAX_GROUPS) {
        return t('tools.content_groups.errors.too_many_groups', { max: MAX_GROUPS });
    }
    if (existing && existing.rules.length >= MAX_RULES_PER_GROUP) {
        return t('tools.content_groups.errors.too_many_rules', {
            group: rule.group,
            max: MAX_RULES_PER_GROUP,
        });
    }

    const duplicate = existing?.rules.some(
        (item) => item.operator === rule.operator && item.value === rule.value,
    );
    if (duplicate) return t('tools.content_groups.errors.duplicate');

    return null;
}

/**
 * Whether one returned URL meets one rule, checked the way Search Console documents it: contains
 * and not-contains ignore case, equals and the prefix do not, and the expressions are read with
 * RegExp as above.
 */
function matchesRule(url, rule) {
    const text = url.toLowerCase();
    const value = rule.value.toLowerCase();

    switch (rule.operator) {
        case OPERATOR.STARTS_WITH:
            return url.startsWith(rule.value);
        case OPERATOR.EQUALS:
            return url === rule.value;
        case OPERATOR.CONTAINS:
            return text.includes(value);
        case OPERATOR.NOT_CONTAINS:
            return !text.includes(value);
        case OPERATOR.MATCHES_REGEX:
            return readPattern(rule.value).test(url);
        case OPERATOR.EXCLUDES_REGEX:
            return !readPattern(rule.value).test(url);
        default:
            return false;
    }
}

/**
 * The other groups one returned URL also meets, by name.
 *
 * This is how an overlap is shown without being acted on. The check runs in the browser over a
 * URL Search Console already returned for the active group, and it never moves the URL, subtracts
 * it from another group's figures or deduplicates anything — each group's numbers are its own
 * response. A group whose expression RegExp cannot read is left out of the check rather than
 * guessed at.
 */
export function otherGroupsFor(url, groups, activeName) {
    const names = [];

    for (const group of groups) {
        if (group.name === activeName) continue;

        try {
            if (group.rules.every((rule) => matchesRule(url, rule))) names.push(group.name);
        } catch (err) {
            console.log(err);
        }
    }

    return names;
}
